/**
 * Clientes — a base de quem compra na loja.
 *
 * Esta tela é SÓ a listagem, mais a gaveta de ficha. Cadastro e edição moram em
 * rotas próprias (`TelaCadastroCliente.tsx`), e não num `useState` de três
 * modos como antes: formulário sem endereço perde tudo num F5, não volta pelo
 * botão do navegador e não dá para mandar por link. O JSDoc de lá conta a
 * história inteira.
 *
 * Duas colunas do protótipo NÃO existem aqui: "última compra" e "total gasto".
 * As duas dependem do módulo de venda, e a API declara isso no bloco
 * `historico`. A tela mostra o texto de espera uma vez, acima da tabela, em
 * vez de desenhar "R$ 0,00" em todas as linhas — zero numa coluna de valor é
 * uma afirmação, e seria uma afirmação falsa.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { BotaoExportar } from '../../componentes/BotaoExportar'
import type { ColunaExportacao } from '../../componentes/exportacao'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import {
  ESTILO_ETIQUETA,
  carregarClientes,
  carregarFicha,
  type FichaCliente,
  type Filtro,
  type KPI,
  type LinhaCliente,
  type ListaDeClientes,
} from '../../plataforma/clientes'
import { mascararDocumento, mascararTelefone } from '../../plataforma/mascaras'
import { LayoutApp } from '../app/LayoutApp'

/**
 * As colunas do CSV e da view impressa — a mesma leitura que a tabela da
 * listagem faz, só que devolvendo TEXTO em vez de JSX. Constante de módulo:
 * as funções não dependem de nenhuma prop da tela, então recriá-las a cada
 * render não ajudaria nada e só custaria uma alocação por render.
 */
const COLUNAS_EXPORTACAO_CLIENTES: ColunaExportacao<LinhaCliente>[] = [
  { cabecalho: 'Código', valor: (c) => c.codigo },
  { cabecalho: 'Cliente', valor: (c) => c.nome },
  {
    cabecalho: 'Documento',
    valor: (c) =>
      c.documento
        ? `${c.tipo === 'pj' ? 'CNPJ' : 'CPF'} ${mascararDocumento(c.tipo, c.documento)}`
        : c.tipo_rotulo,
  },
  { cabecalho: 'Telefone', valor: (c) => (c.telefone ? mascararTelefone(c.telefone) : '') },
  { cabecalho: 'E-mail', valor: (c) => c.email ?? '' },
  {
    cabecalho: 'Crediário',
    valor: (c) =>
      c.crediario.liberado
        ? `${c.crediario.rotulo} até ${formatarMoeda(c.crediario.limite_centavos)}`
        : c.crediario.rotulo,
  },
  { cabecalho: 'Situação', valor: (c) => c.status_rotulo },
]

const ICONE_KPI: Record<string, NomeIcone> = {
  cadastrados: 'group',
  vip: 'trending_up',
  crediario_liberado: 'payments',
  ticket_medio: 'receipt_long',
}

export function TelaClientes() {
  const navegar = useNavigate()

  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [lista, setLista] = useState<ListaDeClientes | null>(null)
  const [erro, setErro] = useState('')
  // Muda a cada gravação, para a listagem recarregar sem recriar o estado.
  const [versao, setVersao] = useState(0)

  const [aberta, setAberta] = useState<FichaCliente | null>(null)

  // Debounce da busca: cada tecla disparando uma consulta que varre a base é
  // como se derruba o próprio banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()

    carregarClientes(buscaAtiva, filtro, controle.signal)
      .then((dados) => {
        setLista(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os clientes.')
      })

    return () => controle.abort()
  }, [buscaAtiva, filtro, versao])

  const abrirFicha = useCallback(async (codigo: string) => {
    try {
      setAberta(await carregarFicha(codigo))
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir a ficha.')
    }
  }, [])

  // Editar direto da lista pula a gaveta: vai para a rota de edição, em vez de
  // obrigar "ver ficha" → "editar cliente" para uma troca de telefone. Agora é
  // uma navegação de verdade, e não uma troca de `useState` — a edição tem
  // endereço próprio.
  const editarDireto = useCallback(
    (codigo: string) => navegar(`/app/clientes/${encodeURIComponent(codigo)}/editar`),
    [navegar],
  )

  const subtitulo = lista
    ? `${lista.base} ${lista.base === 1 ? 'cliente cadastrado' : 'clientes cadastrados'}`
    : 'Carregando…'

  // Frase de uma linha com o recorte ativo, para o cabeçalho do PDF — "aplicado
  // um chip" e "digitou uma busca" não são a mesma frase, e os dois podem
  // valer ao mesmo tempo.
  const rotuloDoFiltro = lista?.filtros.find((chip) => chip.chave === filtro)?.rotulo
  const filtroAplicado = [
    buscaAtiva && `Busca: "${buscaAtiva}"`,
    filtro !== 'todos' && rotuloDoFiltro && `Filtro: ${rotuloDoFiltro}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <LayoutApp
      titulo="Clientes"
      subtitulo={subtitulo}
      acoes={
        <>
          {lista && (
            <BotaoExportar
              nomeBase="clientes"
              titulo="Clientes"
              filtroAplicado={filtroAplicado || undefined}
              colunas={COLUNAS_EXPORTACAO_CLIENTES}
              linhas={lista.linhas}
            />
          )}
          <LinkBotao para="/app/clientes/novo" icone="person_add">
            Novo cliente
          </LinkBotao>
        </>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {erro && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
          >
            <span className="min-w-0 flex-1">{erro}</span>
            <button
              type="button"
              onClick={() => setVersao((n) => n + 1)}
              className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
            >
              Tentar de novo
            </button>
          </div>
        )}

        {!lista ? (
          <Esqueleto />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {lista.kpis.map((kpi) => (
                <CartaoKPI key={kpi.chave} kpi={kpi} />
              ))}
            </div>

            <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
              <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
                {/* `basis-[240px]` + `min-w-0`: regra 3 do "layout que já
                    custou bug" — input dentro de composto precisa dos dois, ou
                    estica o flex e empurra os chips para fora. */}
                <label className="composto h-[34px] min-w-0 flex-1 basis-[240px] cursor-text">
                  <Icone nome="search" tamanho={18} className="text-texto-fraco" />
                  <input
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                    placeholder="Buscar por nome, telefone ou CPF"
                  />
                  <span className="sr-only">Buscar cliente</span>
                </label>

                {lista.filtros.map((chip) => {
                  const ativo = chip.chave === filtro
                  return (
                    <button
                      key={chip.chave}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => setFiltro(chip.chave)}
                      className={`flex h-[34px] items-center gap-2 rounded-sistema border px-3.5 text-[13.5px] font-semibold ${
                        ativo
                          ? 'border-marca bg-marca-suave text-marca-forte'
                          : 'border-borda bg-superficie text-tinta'
                      }`}
                    >
                      {chip.rotulo}
                      <span
                        className={`tabular rounded-full px-[7px] py-[2px] text-[11px] font-bold ${
                          ativo ? 'bg-marca-barra text-marca-forte' : 'bg-divisoria text-texto-suave'
                        }`}
                      >
                        {chip.total}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/*
                A honestidade da tabela, dita uma vez. O protótipo tem as
                colunas "Última compra" e "Total gasto"; as duas dependem de
                venda, e a API declara isso em `historico.fonte`.
              */}
              {lista.historico.fonte === 'aguarda_venda' && lista.linhas.length > 0 && (
                <div className="flex gap-2.5 border-b border-marca-barra bg-marca-fundo-2 px-4 py-2.5">
                  <Icone nome="info" tamanho={18} className="text-marca" />
                  <p className="text-[12.5px] leading-[1.5] text-marca-forte">
                    Última compra, total gasto e saldo do crediário aparecem quando o PDV existir.{' '}
                    {lista.historico.espera}
                  </p>
                </div>
              )}

              {lista.linhas.length === 0 ? (
                <EstadoVazio
                  base={lista.base}
                  busca={lista.busca ?? ''}
                  filtro={filtro}
                  aoCadastrar={() => navegar('/app/clientes/novo')}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] border-collapse">
                    <caption className="sr-only">Clientes cadastrados na loja</caption>
                    <thead>
                      <tr className="bg-fundo">
                        {['Código', 'Cliente', 'Contato', 'Perfil', 'Crediário', 'Situação', ''].map(
                          (titulo, i) => (
                            <th
                              key={titulo || 'acoes'}
                              scope="col"
                              className={`border-b border-borda py-2.5 text-left text-[12.5px] font-semibold text-texto-suave ${
                                i === 0 ? 'pr-3 pl-4' : 'px-3'
                              }`}
                            >
                              {titulo}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {lista.linhas.map((linha, i) => (
                        <LinhaDaTabela
                          key={linha.codigo}
                          linha={linha}
                          ultima={i === lista.linhas.length - 1}
                          aoAbrir={() => void abrirFicha(linha.codigo)}
                          aoEditar={() => void editarDireto(linha.codigo)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-borda px-4 py-2.5">
                <span className="tabular text-[13px] text-texto-suave">
                  {lista.total} de {lista.base}{' '}
                  {lista.base === 1 ? 'cliente' : 'clientes'}
                </span>
                {lista.total < lista.base && (
                  <span className="text-[12.5px] text-texto-fraco">
                    Refine a busca para ver o resto da base.
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {aberta && <Drawer ficha={aberta} aoFechar={() => setAberta(null)} />}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ KPI */

function CartaoKPI({ kpi }: { kpi: KPI }) {
  const esperando = kpi.fonte !== 'real'
  const valor = kpi.formato === 'dinheiro' ? formatarMoeda(kpi.valor) : String(kpi.valor)

  return (
    <div className="rounded-sistema border border-borda bg-superficie px-4 py-3.5 shadow-[var(--shadow-cartao)]">
      <div className="mb-2.5 flex items-center gap-2 text-[12.5px] text-texto-suave">
        <Icone nome={ICONE_KPI[kpi.chave] ?? 'person'} tamanho={18} className="text-texto-fraco" />
        {kpi.rotulo}
      </div>
      {/*
        Bloco que aguarda venda NÃO mostra o número. Um "R$ 0,00" cinza ainda é
        um valor na tela, e quem olha de longe lê como medição.
      */}
      {esperando ? (
        <div className="text-[15px] font-semibold text-texto-fraco">—</div>
      ) : (
        <div className="tabular text-[21px] font-bold text-tinta">{valor}</div>
      )}
      <div className="mt-1 text-xs text-texto-suave">{kpi.espera ?? kpi.detalhe ?? ''}</div>
    </div>
  )
}

/* --------------------------------------------------------------- tabela */

function LinhaDaTabela({
  linha,
  ultima,
  aoAbrir,
  aoEditar,
}: {
  linha: LinhaCliente
  ultima: boolean
  aoAbrir: () => void
  aoEditar: () => void
}) {
  const borda = ultima ? '' : 'border-b border-divisoria'

  return (
    <tr>
      {/*
        O código é a primeira coluna, e é `tabular`: é o que o balcão dita ao
        telefone e o que a pessoa procura correndo o olho pela lista. Cifra de
        largura fixa impede a coluna de dançar entre C0009 e C0010.
      */}
      <td className={`tabular py-2.5 pr-3 pl-4 text-[13px] font-semibold text-texto-suave ${borda}`}>
        {linha.codigo}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <button
          type="button"
          onClick={aoAbrir}
          className="text-left text-[13.5px] font-semibold text-tinta hover:text-marca"
        >
          {linha.nome}
        </button>
        <div className="tabular mt-0.5 text-xs text-texto-suave">
          {linha.documento
            ? `${linha.tipo === 'pj' ? 'CNPJ' : 'CPF'} ${mascararDocumento(linha.tipo, linha.documento)}`
            : linha.tipo_rotulo}
        </div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <div className="tabular text-[13px] text-tinta">
          {linha.telefone ? mascararTelefone(linha.telefone) : '—'}
        </div>
        {linha.email && (
          <div className="mt-0.5 max-w-[180px] truncate text-xs text-texto-suave">
            {linha.email}
          </div>
        )}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <div className="flex flex-wrap gap-1.5">
          {linha.etiquetas.map((etiqueta) => (
            <Etiqueta key={etiqueta} nome={etiqueta} />
          ))}
        </div>
        <div className="mt-1 text-xs text-texto-suave">{grade(linha)}</div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <SeloCrediario linha={linha} />
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <span
          className={`inline-flex items-center rounded-[6px] border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
            linha.status === 'ativo'
              ? 'border-ok-suave bg-ok-suave text-ok-texto'
              : 'border-borda bg-superficie text-texto-suave'
          }`}
        >
          {linha.status_rotulo}
        </span>
      </td>

      <td className={`px-3 py-2.5 text-right ${borda}`}>
        <MenuAcoesLinha nome={linha.nome} aoAbrir={aoAbrir} aoEditar={aoEditar} />
      </td>
    </tr>
  )
}

/**
 * Menu de ações da linha: três pontos, popover com "Ver ficha" e "Editar".
 *
 * Um botão por ação (o que a linha tinha antes) não escala — a próxima ação
 * que a tela ganhar (inativar, imprimir etiqueta) empurraria a tabela para
 * fora da largura da tela. O popover cresce sem mexer na tabela.
 *
 * Vai por `createPortal` para o `<body>`, com posição calculada a partir do
 * botão. A linha vive dentro do `overflow-x-auto` que dá scroll horizontal à
 * tabela — um popover `absolute` comum fica cortado pela borda daquele
 * contêiner, porque `overflow-x` diferente de `visible` também limita o eixo
 * vertical (é a regra do CSS, não um bug do navegador). Fora da árvore da
 * tabela, o corte desaparece.
 */
function MenuAcoesLinha({
  nome,
  aoAbrir,
  aoEditar,
}: {
  nome: string
  aoAbrir: () => void
  aoEditar: () => void
}) {
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState({ top: 0, right: 0 })
  const botaoRef = useRef<HTMLButtonElement>(null)

  const abrir = () => {
    const retangulo = botaoRef.current?.getBoundingClientRect()
    if (retangulo) {
      setPosicao({ top: retangulo.bottom + 4, right: window.innerWidth - retangulo.right })
    }
    setAberto(true)
  }

  // Rolar a tabela (ou a janela) com o menu aberto deixaria o popover flutuando
  // longe do botão que o abriu, já que a posição foi calculada uma vez só.
  // Fechar é mais simples e mais correto do que recalcular a cada pixel de scroll.
  useEffect(() => {
    if (!aberto) return
    const fechar = () => setAberto(false)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    return () => {
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
    }
  }, [aberto])

  return (
    <>
      <button
        ref={botaoRef}
        type="button"
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-expanded={aberto}
        aria-haspopup="menu"
        className="flex h-8 w-8 items-center justify-center rounded-sistema text-texto-suave hover:bg-divisoria hover:text-tinta"
      >
        <Icone nome="more_vert" tamanho={19} />
        <span className="sr-only">Ações de {nome}</span>
      </button>

      {aberto &&
        createPortal(
          <>
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setAberto(false)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              role="menu"
              style={{ top: posicao.top, right: posicao.right }}
              className="fixed z-50 w-[170px] rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false)
                  aoAbrir()
                }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] font-medium text-texto hover:bg-divisoria"
              >
                <Icone nome="visibility" tamanho={17} />
                Ver ficha
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false)
                  aoEditar()
                }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] font-medium text-texto hover:bg-divisoria"
              >
                <Icone nome="edit_note" tamanho={17} />
                Editar cliente
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  )
}

/** "M · 38", "Grade fechada" ou travessão — o que a loja usa para avisar. */
function grade(linha: LinhaCliente): string {
  const partes = [linha.tamanho_preferido, linha.numeracao_preferida].filter(Boolean)
  if (partes.length === 0) return 'Sem preferência de grade'
  return partes.join(' · ')
}

function Etiqueta({ nome }: { nome: string }) {
  const estilo = ESTILO_ETIQUETA[nome] ?? 'bg-divisoria text-texto-suave'
  return (
    <span
      className={`rounded-full px-2 py-[2px] text-[10.5px] font-bold tracking-[0.3px] uppercase ${estilo}`}
    >
      {nome}
    </span>
  )
}

/**
 * Selo de crediário.
 *
 * Mostra o que É verdade: liberado ou não, e até quanto. "Em dia", "A vencer" e
 * "Atrasado" — os três estados do protótipo — dependem de parcela em aberto, e
 * parcela vem de venda a prazo. Inventar o estado aqui seria dizer ao lojista
 * que o cliente está em dia sem ter olhado nada.
 */
function SeloCrediario({ linha }: { linha: LinhaCliente }) {
  if (!linha.crediario.liberado) {
    return (
      <span className="inline-flex items-center rounded-[6px] border border-borda bg-superficie px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-texto-fraco">
        {linha.crediario.rotulo}
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-[6px] border border-marca-barra bg-marca-fundo-2 px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-marca-forte">
      {linha.crediario.rotulo}
      <span className="tabular">
        até {formatarMoeda(linha.crediario.limite_centavos)}
      </span>
    </span>
  )
}

/* ---------------------------------------------------------- estado vazio */

function EstadoVazio({
  base,
  busca,
  filtro,
  aoCadastrar,
}: {
  base: number
  busca: string
  filtro: Filtro
  aoCadastrar: () => void
}) {
  // Vazio INICIAL é diferente de vazio por filtro: o primeiro explica o que a
  // tela faz e oferece a ação; o segundo manda ajustar a busca. Tratar os dois
  // igual é como a pessoa conclui que o sistema não funciona.
  const inicial = base === 0 && busca === '' && filtro === 'todos'

  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <Icone
        nome={inicial ? 'group' : 'person_search'}
        tamanho={26}
        className="text-inativo"
      />
      <p className="text-sm font-semibold text-tinta">
        {inicial ? 'Nenhum cliente cadastrado' : 'Nenhum cliente encontrado'}
      </p>
      <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
        {inicial
          ? 'Cadastre quem compra na loja para guardar contato, tamanhos preferidos e crediário — e para o PDV achar o cliente na hora da venda.'
          : 'Ajuste a busca ou remova os filtros ativos.'}
      </p>
      {inicial && (
        <div className="mt-2">
          <Botao icone="person_add" onClick={aoCadastrar}>
            Cadastrar o primeiro cliente
          </Botao>
        </div>
      )}
    </div>
  )
}

/** Skeleton de linha, nunca spinner de página inteira (spec 03). */
function Esqueleto() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[104px] animate-pulse rounded-sistema bg-superficie" />
        ))}
      </div>
      <div className="h-[280px] animate-pulse rounded-sistema bg-superficie" />
    </div>
  )
}

/* --------------------------------------------------------------- drawer */

function Drawer({ ficha, aoFechar }: { ficha: FichaCliente; aoFechar: () => void }) {
  return (
    <Gaveta rotulo={`Ficha de ${ficha.nome}`} aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo={ficha.nome}
        subtitulo={`Ficha do cliente ${ficha.codigo}`}
        rotuloFechar="Fechar ficha"
        aoFechar={aoFechar}
      />

      <CorpoDaGaveta>
          <div className="mb-5">
            <div className="flex items-baseline gap-2.5">
              <span className="font-titulo text-[19px] font-semibold text-tinta">
                {ficha.nome}
              </span>
              {/* O código ao lado do nome, e não escondido no rodapé: é por ele
                  que a loja se refere ao cliente no telefone. */}
              <span className="tabular rounded-[6px] bg-divisoria px-2 py-[2px] text-[12px] font-bold text-texto-suave">
                {ficha.codigo}
              </span>
            </div>
            <div className="tabular mt-1 text-[13px] text-texto-suave">
              {ficha.documento
                ? `${ficha.tipo === 'pj' ? 'CNPJ' : 'CPF'} ${mascararDocumento(ficha.tipo, ficha.documento)}`
                : ficha.tipo_rotulo}
            </div>
            {ficha.etiquetas.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {ficha.etiquetas.map((etiqueta) => (
                  <Etiqueta key={etiqueta} nome={etiqueta} />
                ))}
              </div>
            )}
          </div>

          <Secao titulo="Contato">
            <LinhaFicha
              rotulo="Telefone"
              valor={ficha.contato.telefone ? mascararTelefone(ficha.contato.telefone) : '—'}
            />
            <LinhaFicha rotulo="E-mail" valor={ficha.contato.email ?? '—'} />
            {/* Os dois consentimentos, lado a lado e separados. */}
            <LinhaFicha
              rotulo="Cupom por WhatsApp"
              valor={ficha.contato.aceita_whatsapp ? 'Autorizado' : 'Não autorizado'}
            />
            <LinhaFicha
              rotulo="Campanhas"
              valor={ficha.contato.aceita_marketing ? 'Autorizado' : 'Não autorizado'}
            />
          </Secao>

          <Secao titulo="Endereço">
            {ficha.endereco.completo || ficha.endereco.cep ? (
              <>
                <LinhaFicha
                  rotulo="Logradouro"
                  valor={[ficha.endereco.logradouro, ficha.endereco.numero]
                    .filter(Boolean)
                    .join(', ') || '—'}
                />
                <LinhaFicha rotulo="Bairro" valor={ficha.endereco.bairro ?? '—'} />
                <LinhaFicha
                  rotulo="Cidade"
                  valor={[ficha.endereco.cidade, ficha.endereco.uf].filter(Boolean).join(' / ') || '—'}
                />
                <LinhaFicha rotulo="CEP" valor={ficha.endereco.cep ?? '—'} />
              </>
            ) : (
              <p className="py-2 text-[13px] text-texto-suave">
                Sem endereço. É o que falta para vender a prazo.
              </p>
            )}
          </Secao>

          <Secao titulo="Preferências de grade">
            <LinhaFicha rotulo="Tamanho de roupa" valor={ficha.preferencia.tamanho || '—'} />
            <LinhaFicha rotulo="Numeração de tênis" valor={ficha.preferencia.numeracao || '—'} />
            <LinhaFicha
              rotulo="Cores preferidas"
              valor={ficha.preferencia.cores.join(', ') || '—'}
            />
          </Secao>

          <Secao titulo="Crediário">
            <div
              className={`rounded-sistema border px-4 py-3.5 ${
                ficha.crediario.liberado
                  ? 'border-marca-barra bg-marca-fundo-2'
                  : 'border-borda bg-superficie-suave'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className={`text-[13.5px] font-semibold ${
                      ficha.crediario.liberado ? 'text-marca-forte' : 'text-texto-suave'
                    }`}
                  >
                    {ficha.crediario.rotulo}
                  </div>
                  <div className="mt-1 text-[12.5px] text-texto-suave">
                    {ficha.crediario.liberado
                      ? `Até ${ficha.crediario.parcelas_max}x${
                          ficha.crediario.dia_vencimento
                            ? ` · vence dia ${ficha.crediario.dia_vencimento}`
                            : ''
                        }`
                      : 'Este cliente não compra a prazo.'}
                  </div>
                </div>
                {ficha.crediario.liberado && (
                  <div className="tabular shrink-0 font-titulo text-[19px] font-bold text-tinta">
                    {formatarMoeda(ficha.crediario.limite_centavos)}
                  </div>
                )}
              </div>
            </div>
            {/*
              O limite CONFIGURADO está acima e é real. O USADO não existe: sem
              venda a prazo não há parcela em aberto para somar, e um "R$ 0,00
              em aberto" seria uma afirmação sem lastro.
            */}
            {ficha.crediario.fonte_saldo === 'aguarda_venda' && (
              <p className="mt-2.5 text-[12px] leading-[1.5] text-texto-fraco">
                Saldo em aberto e próxima parcela: {ficha.crediario.espera_saldo}
              </p>
            )}
          </Secao>

          <Secao titulo="Últimas compras">
            {ficha.compras.linhas.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-sistema border border-dashed border-borda px-4 py-7 text-center">
                <Icone nome="receipt_long" tamanho={22} className="text-inativo" />
                <p className="max-w-[280px] text-[12.5px] leading-[1.5] text-texto-suave">
                  {ficha.compras.espera}
                </p>
              </div>
            ) : (
              ficha.compras.linhas.map((compra) => (
                <div
                  key={compra.quando + compra.resumo}
                  className="flex gap-3 border-t border-divisoria py-2.5 first:border-t-0"
                >
                  <span className="tabular w-14 shrink-0 text-[12.5px] text-texto-suave">
                    {compra.quando.slice(0, 10)}
                  </span>
                  <span className="min-w-0 flex-1 text-[13px] text-tinta">{compra.resumo}</span>
                  <span className="tabular shrink-0 text-[13px] font-semibold text-tinta">
                    {formatarMoeda(compra.total_centavos)}
                  </span>
                </div>
              ))
            )}
          </Secao>

          {ficha.observacao_interna && (
            <Secao titulo="Observação interna">
              <p className="text-[13px] leading-[1.6] text-texto">{ficha.observacao_interna}</p>
              <p className="mt-2 text-[12px] text-texto-fraco">
                Visível só para a equipe, nunca no cupom.
              </p>
            </Secao>
          )}
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
        {/*
          "Nova venda" é o CTA do protótipo, e o PDV não existe. Um botão que
          não faz nada ensina que a função existe e depois some — então o
          lugar é da ação que existe de verdade.

          É um LINK e não um botão: a edição tem endereço próprio
          (`/app/clientes/:codigo/editar`), e link é o que permite abrir em
          outra aba e voltar pelo navegador.
        */}
        <LinkBotao
          para={`/app/clientes/${encodeURIComponent(ficha.codigo)}/editar`}
          icone="edit_note"
        >
          Editar cliente
        </LinkBotao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="mb-3 text-[11px] font-bold tracking-[0.6px] text-texto-fraco uppercase">
        {titulo}
      </h3>
      {children}
    </section>
  )
}

function LinhaFicha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-4 border-t border-divisoria py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular min-w-0 text-right font-semibold break-words text-tinta">
        {valor}
      </span>
    </div>
  )
}
